import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Copy } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import ImageGalleryManager from './ImageGalleryManager';
import { ContentBlockManager } from './ContentBlockManager';
import { ProductContentSection, SpecItem, FeatureItem, ContentBlock } from '@/types/modernProductContent';
import { motion } from 'framer-motion';

interface SectionContentEditorProps {
  section: ProductContentSection;
  onUpdate: (section: ProductContentSection) => void;
}

export const SectionContentEditor: React.FC<SectionContentEditorProps> = ({
  section,
  onUpdate
}) => {
  const [contentType, setContentType] = useState<'rich_text' | 'html' | 'markdown' | 'structured_data'>('rich_text');

  const updateContent = (newContent: any) => {
    const updatedSection = {
      ...section,
      content: newContent,
      updated_at: new Date().toISOString()
    };
    onUpdate(updatedSection);
  };

  const renderDescriptionEditor = () => (
    <div className="space-y-4">
      <RichTextEditor
        label="Product Description"
        value={section.content.description || ''}
        onChange={(value) => updateContent({ ...section.content, description: value })}
        contentType={contentType}
        onContentTypeChange={setContentType}
        placeholder="Enter a compelling product description..."
      />
    </div>
  );

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <RichTextEditor
        label="Hero Description"
        value={section.content.description || ''}
        onChange={(value) => updateContent({ ...section.content, description: value })}
        contentType={contentType}
        onContentTypeChange={setContentType}
        placeholder="Write an engaging overview of your product..."
      />
      
      <div className="space-y-4">
        <Label className="text-base font-semibold">Key Highlights</Label>
        {(section.content.highlights || []).map((highlight: string, index: number) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex gap-2"
          >
            <Input
              value={highlight}
              onChange={(e) => {
                const newHighlights = [...(section.content.highlights || [])];
                newHighlights[index] = e.target.value;
                updateContent({ ...section.content, highlights: newHighlights });
              }}
              placeholder="Key product highlight"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newHighlights = (section.content.highlights || []).filter((_: any, i: number) => i !== index);
                updateContent({ ...section.content, highlights: newHighlights });
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </motion.div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            const newHighlights = [...(section.content.highlights || []), ''];
            updateContent({ ...section.content, highlights: newHighlights });
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Highlight
        </Button>
      </div>
    </div>
  );

  const renderSpecsEditor = () => (
    <div className="space-y-4">
      <Label className="text-base font-semibold">Product Specifications</Label>
      <div className="space-y-4">
        {(section.content.specs || []).map((spec: SpecItem, index: number) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg bg-muted/30"
          >
            <div>
              <Label>Specification Name</Label>
              <Input
                value={spec.key}
                onChange={(e) => {
                  const newSpecs = [...(section.content.specs || [])];
                  newSpecs[index] = { ...spec, key: e.target.value };
                  updateContent({ ...section.content, specs: newSpecs });
                }}
                placeholder="e.g., Weight, Size, Material"
              />
            </div>
            <div>
              <Label>Value</Label>
              <Input
                value={spec.value}
                onChange={(e) => {
                  const newSpecs = [...(section.content.specs || [])];
                  newSpecs[index] = { ...spec, value: e.target.value };
                  updateContent({ ...section.content, specs: newSpecs });
                }}
                placeholder="e.g., 2.5kg, Large, Aluminum"
              />
            </div>
            <div>
              <Label>Unit (Optional)</Label>
              <Input
                value={spec.unit || ''}
                onChange={(e) => {
                  const newSpecs = [...(section.content.specs || [])];
                  newSpecs[index] = { ...spec, unit: e.target.value };
                  updateContent({ ...section.content, specs: newSpecs });
                }}
                placeholder="kg, cm, mm"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newSpecs = [...(section.content.specs || [])];
                  newSpecs[index] = { ...spec, copyable: !spec.copyable };
                  updateContent({ ...section.content, specs: newSpecs });
                }}
                className={spec.copyable ? 'bg-primary text-primary-foreground' : ''}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newSpecs = (section.content.specs || []).filter((_: any, i: number) => i !== index);
                  updateContent({ ...section.content, specs: newSpecs });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            const newSpecs = [...(section.content.specs || []), { key: '', value: '', featured: false, copyable: true }];
            updateContent({ ...section.content, specs: newSpecs });
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Specification
        </Button>
      </div>
    </div>
  );

  const renderFeaturesEditor = () => (
    <div className="space-y-4">
      <Label className="text-base font-semibold">Product Features</Label>
      <div className="space-y-4">
        {(section.content.features || []).map((feature: FeatureItem, index: number) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 border rounded-lg bg-muted/30 space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Feature Title</Label>
                <Input
                  value={feature.title}
                  onChange={(e) => {
                    const newFeatures = [...(section.content.features || [])];
                    newFeatures[index] = { ...feature, title: e.target.value };
                    updateContent({ ...section.content, features: newFeatures });
                  }}
                  placeholder="Feature name"
                />
              </div>
              <div>
                <Label>Feature Type</Label>
                <select
                  value={feature.type}
                  onChange={(e) => {
                    const newFeatures = [...(section.content.features || [])];
                    newFeatures[index] = { ...feature, type: e.target.value as any };
                    updateContent({ ...section.content, features: newFeatures });
                  }}
                  className="w-full p-2 border rounded-md bg-background"
                >
                  <option value="highlight">Highlight</option>
                  <option value="benefit">Benefit</option>
                  <option value="technical">Technical</option>
                  <option value="usp">Unique Selling Point</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={feature.description || ''}
                onChange={(e) => {
                  const newFeatures = [...(section.content.features || [])];
                  newFeatures[index] = { ...feature, description: e.target.value };
                  updateContent({ ...section.content, features: newFeatures });
                }}
                placeholder="Describe this feature..."
                rows={3}
              />
            </div>
            <div className="flex justify-between items-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={feature.featured}
                  onChange={(e) => {
                    const newFeatures = [...(section.content.features || [])];
                    newFeatures[index] = { ...feature, featured: e.target.checked };
                    updateContent({ ...section.content, features: newFeatures });
                  }}
                />
                <span className="text-sm">Featured</span>
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newFeatures = (section.content.features || []).filter((_: any, i: number) => i !== index);
                  updateContent({ ...section.content, features: newFeatures });
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        ))}
        <Button
          variant="outline"
          onClick={() => {
            const newFeatures = [...(section.content.features || []), { title: '', description: '', type: 'highlight', featured: false }];
            updateContent({ ...section.content, features: newFeatures });
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Feature
        </Button>
      </div>
    </div>
  );

  const renderStoryEditor = () => (
    <div className="space-y-4">
      <RichTextEditor
        label="Product Story"
        value={section.content.story || ''}
        onChange={(value) => updateContent({ ...section.content, story: value })}
        contentType={contentType}
        onContentTypeChange={setContentType}
        placeholder="Tell the story behind your product..."
      />
    </div>
  );

  const renderTechnicalEditor = () => (
    <div className="space-y-4">
      <RichTextEditor
        label="Technical Information"
        value={section.content.technical || ''}
        onChange={(value) => updateContent({ ...section.content, technical: value })}
        contentType={contentType}
        onContentTypeChange={setContentType}
        placeholder="Provide detailed technical specifications..."
      />
    </div>
  );

  const renderGalleryEditor = () => (
    <div className="space-y-6">
      <div className="grid gap-4">
        <div>
          <Label>Gallery Images</Label>
          <ImageGalleryManager 
            images={section.content.gallery_images || []}
            onImagesChange={(images) => updateContent({ ...section.content, gallery_images: images })}
          />
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="gallery-style">Display Style</Label>
            <Select
              value={section.layout_config?.style || 'grid'}
              onValueChange={(value) => updateContent({ 
                ...section.content,
                layout_config: { ...section.layout_config, style: value }
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Grid Layout</SelectItem>
                <SelectItem value="masonry">Masonry Layout</SelectItem>
                <SelectItem value="carousel">Carousel</SelectItem>
                <SelectItem value="lightbox">Lightbox Gallery</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="gallery-columns">Columns</Label>
            <Select
              value={section.layout_config?.columns?.toString() || '3'}
              onValueChange={(value) => updateContent({ 
                ...section.content,
                layout_config: { ...section.layout_config, columns: parseInt(value) }
              })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 Columns</SelectItem>
                <SelectItem value="3">3 Columns</SelectItem>
                <SelectItem value="4">4 Columns</SelectItem>
                <SelectItem value="5">5 Columns</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuaranteesEditor = () => (
    <div className="space-y-4">
      <RichTextEditor
        label="Warranties & Guarantees"
        value={section.content.guarantees || ''}
        onChange={(value) => updateContent({ ...section.content, guarantees: value })}
        contentType={contentType}
        onContentTypeChange={setContentType}
        placeholder="Describe warranties, guarantees, and trust signals..."
      />
    </div>
  );

  const renderSectionContent = () => {
    switch (section.section_type) {
      case 'description':
        return renderDescriptionEditor();
      case 'hero':
        return renderHeroEditor();
      case 'specs':
        return renderSpecsEditor();
      case 'features':
        return renderFeaturesEditor();
      case 'story':
        return renderStoryEditor();
      case 'technical':
        return renderTechnicalEditor();
      case 'gallery':
        return renderGalleryEditor();
      case 'guarantees':
        return renderGuaranteesEditor();
      default:
        return (
          <div className="p-8 text-center text-muted-foreground">
            <p>Content editor for "{section.section_type}" not implemented yet</p>
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <Card className="p-6">
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-foreground mb-2">
            {section.title}
          </h4>
          {section.subtitle && (
            <p className="text-muted-foreground">{section.subtitle}</p>
          )}
        </div>
        
        {renderSectionContent()}
      </Card>
    </motion.div>
  );
};